import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ institutions, onRemove, onClear, isOpen, onToggle }) => {
  if (institutions?.length === 0) return null;

  const formatFees = (fees) => {
    if (fees === 0) return 'Gratuit';
    return `${fees?.toLocaleString('fr-MA')} MAD/an`;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'universite-publique': 'Université Publique',
      'universite-privee': 'Université Privée',
      'ecole-ingenieur': 'École d\'Ingénieur',
      'ecole-commerce': 'École de Commerce',
      'institut-specialise': 'Institut Spécialisé',
      'ecole-art': 'École d\'Art',
      'formation-professionnelle': 'Formation Pro'
    };
    return labels?.[type] || type;
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      'facile': 'Facile',
      'moyen': 'Moyen',
      'difficile': 'Difficile'
    };
    return labels?.[difficulty] || difficulty;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'facile': 'text-green-600',
      'moyen': 'text-orange-600',
      'difficile': 'text-red-600'
    };
    return colors?.[difficulty] || 'text-gray-600';
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 left-6 z-40">
        <Button
          onClick={onToggle}
          iconName="GitCompare"
          iconPosition="left"
          className="shadow-interactive"
        >
          Comparer ({institutions?.length})
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-interactive w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            Comparaison des établissements ({institutions?.length}/3)
          </h2>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={onClear} iconName="Trash2" size="sm">
              Tout effacer
            </Button>
            <Button variant="ghost" size="icon" onClick={onToggle}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {institutions?.map((institution) => (
              <div key={institution?.id} className="border border-border rounded-lg p-4">
                {/* Institution Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 overflow-hidden rounded-lg">
                      <Image
                        src={institution?.image}
                        alt={institution?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm">{institution?.name}</h3>
                      <p className="text-xs text-muted-foreground">{institution?.city}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(institution?.id)}
                    className="h-8 w-8"
                  >
                    <Icon name="X" size={14} />
                  </Button>
                </div>

                {/* Comparison Details */}
                <div className="space-y-4">
                  {/* Type */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">TYPE</h4>
                    <p className="text-sm">{getTypeLabel(institution?.type)}</p>
                  </div>

                  {/* Fees */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">FRAIS ANNUELS</h4>
                    <p className="text-sm font-medium">{formatFees(institution?.fees)}</p>
                  </div>

                  {/* Admission Difficulty */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">DIFFICULTÉ</h4>
                    <p className={`text-sm font-medium ${getDifficultyColor(institution?.admissionDifficulty)}`}>
                      {getDifficultyLabel(institution?.admissionDifficulty)}
                    </p>
                  </div>

                  {/* Previous Threshold */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">SEUIL 2023</h4>
                    <p className="text-sm font-medium">{institution?.previousThreshold || 'N/A'}/20</p>
                  </div>

                  {/* Accepted Bac Types */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">BAC ACCEPTÉS</h4>
                    <div className="space-y-1">
                      {institution?.acceptedBacTypes?.slice(0, 3)?.map((bac, index) => (
                        <p key={index} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          {bac}
                        </p>
                      ))}
                      {institution?.acceptedBacTypes?.length > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{institution?.acceptedBacTypes?.length - 3} autres
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Top Programs */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">PROGRAMMES POPULAIRES</h4>
                    <div className="space-y-1">
                      {institution?.programs?.slice(0, 3)?.map((program, index) => (
                        <p key={index} className="text-xs">{program}</p>
                      ))}
                      {institution?.programs?.length > 3 && (
                        <p className="text-xs text-primary">+{institution?.programs?.length - 3} autres</p>
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">SERVICES</h4>
                    <div className="space-y-1">
                      {institution?.hasScholarships && (
                        <div className="flex items-center text-xs text-success">
                          <Icon name="Check" size={12} className="mr-1" />
                          <span>Bourses</span>
                        </div>
                      )}
                      {institution?.hasInternships && (
                        <div className="flex items-center text-xs text-primary">
                          <Icon name="Check" size={12} className="mr-1" />
                          <span>Stages</span>
                        </div>
                      )}
                      {institution?.hasExchange && (
                        <div className="flex items-center text-xs text-secondary">
                          <Icon name="Check" size={12} className="mr-1" />
                          <span>Échange</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-4 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="ExternalLink"
                    iconPosition="left"
                  >
                    Voir détails
                  </Button>
                </div>
              </div>
            ))}

            {/* Add More Placeholder */}
            {institutions?.length < 3 && (
              <div className="border-2 border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-center min-h-[400px]">
                <Icon name="Plus" size={32} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-1">Ajouter un établissement</p>
                <p className="text-xs text-muted-foreground">
                  Cliquez sur "Comparer" sur une fiche établissement
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Vous pouvez comparer jusqu'à 3 établissements simultanément
          </p>
          <Button onClick={onToggle}>
            Fermer la comparaison
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTool;